import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import accountingService, { BankAccountDto, CashboxDto } from '../../../services/accountingService';
import {
  CourierPayout,
  CourierPayoutCandidate,
  CourierPayoutInvoiceOption,
  CourierPayoutMethod,
  CourierPayoutStatus,
  courierModuleService,
  formatMoney,
} from './courierModuleService';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Şube yöneticisi: hakediş yalnız bu şube için hazırlanır. */
  lockedBranchId?: string | null;
}

const statusColor: Record<CourierPayoutStatus, 'warning' | 'info' | 'success' | 'default'> = {
  pending: 'warning',
  approved: 'info',
  paid: 'success',
  cancelled: 'default',
};

const day = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');
const trDate = (iso?: string | null) => {
  const d = day(iso);
  return d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '';
};
const trDateTime = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(new Date(iso).getTime() + 3 * 3600 * 1000).toISOString();
  return `${trDate(d)} ${d.slice(11, 16)}`;
};
const num = (v: string): number | null => {
  const s = v.replace(/\s/g, '').replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

/** Teslimat listesini Excel'in açtığı CSV olarak indirir (firmanın listesiyle karşılaştırma için). */
const downloadCsv = (p: CourierPayout, branchName: (id?: string | null) => string) => {
  const head = ['Tarih', 'Şube', 'Kurye', 'Kaynak', 'Sipariş', 'Bölge', 'Mahalle', 'Durum', 'Ücret'];
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const rows = p.deliveries.map((d) => [
    trDateTime(d.at), branchName(d.branchId), d.courierName || '', d.orderSource, d.orderNumber || '',
    d.zoneName || '', d.neighborhood || '', d.status === 'cancelled' ? 'İptal (ücretli)' : 'Teslim', d.fee.toFixed(2).replace('.', ','),
  ].map((v) => esc(String(v))).join(';'));
  const csv = '﻿' + [head.map(esc).join(';'), ...rows].join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `hakedis_${(p.courierName || p.courierId).toString().replace(/\s+/g, '_')}_${day(p.periodStart)}_${day(p.periodEnd)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Kurye hakedişi: kurye ve dönem seçilir, teslimat defterinden hesaplanır, onaylanınca carisine yazılır;
 * alış faturası bağlanır ve ödeme yapılır. Kâr-zarar gideri teslimat gününe göre defterden gelir.
 */
const CourierPayoutDialog: React.FC<Props> = ({ open, onClose, lockedBranchId }) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();
  const [candidates, setCandidates] = useState<CourierPayoutCandidate[]>([]);
  const [courierId, setCourierId] = useState<number | null>(null);
  const [history, setHistory] = useState<CourierPayout[]>([]);
  const [current, setCurrent] = useState<CourierPayout | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [branch, setBranch] = useState('');
  const [offset, setOffset] = useState(false);
  const [note, setNote] = useState('');
  const [invoices, setInvoices] = useState<CourierPayoutInvoiceOption[]>([]);
  const [invoiceId, setInvoiceId] = useState('');
  const [cashboxes, setCashboxes] = useState<CashboxDto[]>([]);
  const [banks, setBanks] = useState<BankAccountDto[]>([]);
  const [method, setMethod] = useState<CourierPayoutMethod>('bank');
  const [sourceId, setSourceId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const candidate = candidates.find((c) => c.courierId === courierId) || null;
  const saved = !!current && current.id > 0;
  const branchName = useCallback(
    (id?: string | null) => (branches || []).find((b: any) => b.id === id || b.firebaseId === id)?.name || '',
    [branches],
  );

  const loadCandidates = useCallback(async () => {
    try {
      const list = await courierModuleService.payoutCandidates();
      setCandidates(list);
      return list;
    } catch (e: any) {
      setError(e?.message || t('courierModule.payout.loadFailed'));
      return [];
    }
  }, [t]);

  /** Kurye seçimi: önerilen dönem aynı anda yazılır ki önizleme tek kez, doğru dönemle çalışsın. */
  const selectCourier = useCallback((c: CourierPayoutCandidate) => {
    setCourierId(c.courierId);
    setStart(day(c.suggestedStart));
    setEnd(day(c.suggestedEnd));
    setBranch(lockedBranchId || '');
    setOffset(false);
    setNote('');
    setCurrent(null);
    setConfirmApprove(false);
  }, [lockedBranchId]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setInfo(null);
    setCurrent(null);
    setCourierId(null);
    void loadCandidates().then((list) => { if (list.length > 0) selectCourier(list[0]); });
    accountingService.getCashboxes()
      .then((boxes) => setCashboxes(boxes.filter((c) => !c.IsDeleted && !(c.Name || '').startsWith('Kurye · '))))
      .catch(() => setCashboxes([]));
    accountingService.getBankAccounts()
      .then((list) => setBanks(list.filter((b) => !(b.isDeleted ?? b.IsDeleted))))
      .catch(() => setBanks([]));
  }, [open, loadCandidates, selectCourier]);

  const loadHistory = useCallback(async (id: number) => {
    try {
      setHistory(await courierModuleService.payouts(id));
    } catch {
      setHistory([]);
    }
  }, []);

  // Kurye seçilince geçmiş hakedişleri
  useEffect(() => {
    if (!open || courierId == null) return;
    void loadHistory(courierId);
  }, [open, courierId, loadHistory]);

  const request = useMemo(() => ({
    courierId: courierId ?? 0,
    branchId: branch || null,
    periodStart: start,
    periodEnd: end,
    offsetCashDebt: offset,
    note: note || null,
  }), [courierId, branch, start, end, offset, note]);

  const preview = useCallback(async () => {
    if (courierId == null || !start || !end) return;
    setLoading(true);
    setError(null);
    try {
      setCurrent(await courierModuleService.previewPayout(request));
    } catch (e: any) {
      setCurrent(null);
      setError(e?.message || t('courierModule.payout.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [courierId, start, end, request, t]);

  // Dönem / şube / mahsup değişince önizleme yenilenir (kayıtlı hakediş açıkken değil)
  useEffect(() => {
    if (!open || saved || courierId == null) return;
    void preview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courierId, start, end, branch, offset]);

  const openSaved = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    setConfirmApprove(false);
    try {
      const p = await courierModuleService.payout(id);
      setCurrent(p);
      setAmount('');
      setInvoiceId('');
      setInvoices(p.accountId && !p.invoiceId && p.status !== 'cancelled' ? await courierModuleService.payoutInvoices(id) : []);
    } catch (e: any) {
      setError(e?.message || t('courierModule.payout.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const act = async (fn: () => Promise<CourierPayout>, message: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const p = await fn();
      setInfo(message);
      setConfirmApprove(false);
      await loadCandidates();
      if (courierId != null) await loadHistory(courierId);
      await openSaved(p.id);
    } catch (e: any) {
      setError(e?.message || t('courierModule.payout.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const newPayout = () => {
    setCurrent(null);
    setConfirmApprove(false);
    if (candidate) {
      setStart(day(candidate.suggestedStart));
      setEnd(day(candidate.suggestedEnd));
    }
    void preview();
  };

  const pay = () => {
    if (!current) return;
    const value = amount === '' ? null : num(amount);
    void act(() => courierModuleService.payPayout(current.id, {
      method,
      cashboxId: method === 'cash' && sourceId !== '' ? Number(sourceId) : null,
      bankAccountId: method === 'bank' && sourceId !== '' ? Number(sourceId) : null,
      amount: value,
    }), t('courierModule.payout.paidInfo'));
  };

  const p = current;
  const status = p?.status ?? 'pending';
  const isValet = (p?.courierKind ?? candidate?.kind) === 'PLATFORM_VALET';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" component="span">
          {t('courierModule.payout.title')}{p?.courierName || candidate?.name ? ` · ${p?.courierName || candidate?.name}` : ''}
        </Typography>
        {p && <Chip size="small" label={`${trDate(p.periodStart)} – ${trDate(p.periodEnd)}`} />}
        {p && (
          <Chip size="small" color={statusColor[status]} variant={saved ? 'filled' : 'outlined'}
            label={saved ? t(`courierModule.payout.status.${status}`) : t('courierModule.payout.draft')} />
        )}
        <Box sx={{ flex: 1 }} />
        {p && p.deliveries.length > 0 && (
          <Button size="small" variant="outlined" onClick={() => downloadCsv(p, branchName)}>{t('courierModule.payout.excel')}</Button>
        )}
        {p && !saved && (
          <Button size="small" variant="contained" disabled={busy || loading || (p.packageCount + p.cancelledPaidCount === 0 && p.feeExtras === 0)}
            onClick={() => act(() => courierModuleService.createPayout(request), t('courierModule.payout.createdInfo'))}>
            {t('courierModule.payout.save')}
          </Button>
        )}
        {p && saved && status === 'pending' && (
          <Button size="small" variant="contained" disabled={busy} onClick={() => setConfirmApprove(true)}>
            {t('courierModule.payout.approve')}
          </Button>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {info && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setInfo(null)}>{info}</Alert>}
        {confirmApprove && p && (
          <Alert severity="warning" sx={{ mb: 1.5 }}
            action={(
              <Stack direction="row" spacing={1}>
                <Button size="small" color="inherit" onClick={() => setConfirmApprove(false)}>{t('courierModule.payout.no')}</Button>
                <Button size="small" variant="contained" color="warning" disabled={busy}
                  onClick={() => act(() => courierModuleService.approvePayout(p.id), t('courierModule.payout.approvedInfo'))}>
                  {t('courierModule.payout.approve')}
                </Button>
              </Stack>
            )}>
            {p.accountId ? t('courierModule.payout.approveConfirm', { account: p.accountName || '' }) : t('courierModule.payout.approveConfirmNoAccount')}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 2 }}>
          {/* Sol: kuryeler ve geçmiş hakedişler */}
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">{t('courierModule.payout.couriers')}</Typography>
            {candidates.length === 0 && <Typography variant="body2" color="text.secondary">{t('courierModule.payout.noCouriers')}</Typography>}
            <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 280, overflow: 'auto' }}>
              {candidates.map((c) => (
                <ListItemButton key={c.courierId} selected={c.courierId === courierId} onClick={() => selectCourier(c)}>
                  <ListItemText
                    primary={c.name || `#${c.courierId}`}
                    secondary={`${t(`courierModule.kinds.${c.kind}`)} · ${t('courierModule.payout.openFees', { amount: formatMoney(c.openFees) })}`}
                  />
                  {c.openPayouts > 0 && <Chip size="small" color="warning" label={c.openPayouts} />}
                </ListItemButton>
              ))}
            </List>

            {courierId != null && (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="overline" color="text.secondary">{t('courierModule.payout.history')}</Typography>
                  <Button size="small" onClick={newPayout}>{t('courierModule.payout.new')}</Button>
                </Stack>
                {history.length === 0 && <Typography variant="body2" color="text.secondary">{t('courierModule.payout.noHistory')}</Typography>}
                <List dense disablePadding sx={{ maxHeight: 260, overflow: 'auto' }}>
                  {history.map((h) => (
                    <ListItemButton key={h.id} selected={current?.id === h.id} onClick={() => openSaved(h.id)}>
                      <ListItemText
                        primary={`${trDate(h.periodStart)} – ${trDate(h.periodEnd)}`}
                        secondary={`${formatMoney(h.total)} · ${t(`courierModule.payout.status.${h.status}`)}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
          </Stack>

          {/* Sağ: dönem, şube kırılımı ve özet */}
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            {!saved && courierId != null && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField size="small" type="date" label={t('courierModule.payout.start')} value={start}
                  onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label={t('courierModule.payout.end')} value={end}
                  onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField select size="small" label={t('courierModule.payout.branch')} value={branch}
                  onChange={(e) => setBranch(e.target.value)} sx={{ minWidth: 180 }} disabled={!!lockedBranchId}>
                  {lockedBranchId
                    ? <MenuItem value={lockedBranchId}>{branchName(lockedBranchId) || lockedBranchId}</MenuItem>
                    : <MenuItem value="">{t('courierModule.payout.allBranches')}</MenuItem>}
                  {(branches || []).map((b: any) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                </TextField>
                {candidate?.settlementPeriod && (
                  <Chip size="small" variant="outlined" label={t(`courierModule.agreement.periods.${candidate.settlementPeriod}`)} />
                )}
              </Stack>
            )}

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

            {!loading && p && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 300px' }, gap: 2 }}>
                <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('courierModule.payout.cols.branch')}</TableCell>
                          <TableCell align="right">{t('courierModule.payout.cols.packages')}</TableCell>
                          <TableCell align="right">{t('courierModule.payout.cols.cancelledPaid')}</TableCell>
                          <TableCell align="right">{t('courierModule.payout.cols.fee')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {p.branches.length === 0 && (
                          <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">{t('courierModule.payout.empty')}</Typography></TableCell></TableRow>
                        )}
                        {p.branches.map((b) => (
                          <TableRow key={b.branchId || 'none'}>
                            <TableCell>{b.branchName || branchName(b.branchId) || b.branchId || '—'}</TableCell>
                            <TableCell align="right">{b.packages}</TableCell>
                            <TableCell align="right">{b.cancelledPaid}</TableCell>
                            <TableCell align="right">{formatMoney(b.fee)}</TableCell>
                          </TableRow>
                        ))}
                        {p.feeExtras > 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>{t('courierModule.payout.extras')}</TableCell>
                            <TableCell align="right">{formatMoney(p.feeExtras)}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {p.feeFromCash > 0 && <Alert severity="info">{t('courierModule.payout.fromCashInfo', { amount: formatMoney(p.feeFromCash) })}</Alert>}
                  {!saved && p.cashDebt > 0 && (
                    <Alert severity="warning" action={(
                      <FormControlLabel sx={{ mr: 0 }} control={<Checkbox size="small" checked={offset} onChange={(e) => setOffset(e.target.checked)} />}
                        label={t('courierModule.payout.offset')} />
                    )}>
                      {t('courierModule.payout.cashDebt', { amount: formatMoney(p.cashDebt) })}
                    </Alert>
                  )}
                  {!p.accountId && <Alert severity="info">{t('courierModule.payout.noAccount')}</Alert>}

                  <Box>
                    <Button size="small" onClick={() => setShowDeliveries((v) => !v)}>
                      {t('courierModule.payout.deliveries', { count: p.deliveries.length })}
                    </Button>
                  </Box>
                  {showDeliveries && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('courierModule.payout.cols.date')}</TableCell>
                            <TableCell>{t('courierModule.payout.cols.order')}</TableCell>
                            <TableCell>{t('courierModule.payout.cols.courier')}</TableCell>
                            <TableCell>{t('courierModule.payout.cols.zone')}</TableCell>
                            <TableCell align="right">{t('courierModule.payout.cols.fee')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {p.deliveries.map((d) => (
                            <TableRow key={d.id}>
                              <TableCell>{trDateTime(d.at)}</TableCell>
                              <TableCell>
                                {d.orderNumber}
                                {d.status === 'cancelled' && <Chip size="small" sx={{ ml: 0.5 }} label={t('courierModule.payout.cancelledPaid')} />}
                              </TableCell>
                              <TableCell>{d.courierName}</TableCell>
                              <TableCell>{d.zoneName || d.neighborhood || ''}</TableCell>
                              <TableCell align="right">{formatMoney(d.fee)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {!saved && (
                    <TextField size="small" label={t('courierModule.payout.note')} value={note} onChange={(e) => setNote(e.target.value)} />
                  )}
                </Stack>

                {/* Özet · Fatura · Ödeme */}
                <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, alignSelf: 'start' }}>
                  <Typography variant="overline" color="text.secondary">{t('courierModule.payout.summary')}</Typography>
                  <Row label={p.vatIncluded ? t('courierModule.payout.feeVatIncl') : t('courierModule.payout.feeVatExcl')} value={formatMoney(p.feeNet)} />
                  {p.vatRate > 0 && (
                    <Row label={p.vatIncluded ? t('courierModule.payout.vatInside', { rate: p.vatRate }) : t('courierModule.payout.vat', { rate: p.vatRate })}
                      value={formatMoney(p.vatAmount)} muted={p.vatIncluded} />
                  )}
                  <Row label={t('courierModule.payout.total')} value={formatMoney(p.total)} bold />
                  {p.invoiceTotal != null && <Row label={t('courierModule.payout.invoiceTotal')} value={formatMoney(p.invoiceTotal)} />}
                  {p.feeFromCash > 0 && <Row label={t('courierModule.payout.fromCash')} value={`−${formatMoney(p.feeFromCash)}`} />}
                  {p.cashOffset > 0 && <Row label={t('courierModule.payout.offsetLine')} value={`−${formatMoney(p.cashOffset)}`} />}
                  <Divider />
                  <Row label={t('courierModule.payout.payable')} value={formatMoney(p.payable)} bold />
                  {saved && <Row label={t('courierModule.payout.paid')} value={formatMoney(p.paid)} />}
                  {saved && p.remaining > 0 && <Row label={t('courierModule.payout.remaining')} value={formatMoney(p.remaining)} bold />}

                  {saved && status !== 'cancelled' && (
                    <>
                      <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>{t('courierModule.payout.invoice')}</Typography>
                      {p.invoiceId ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip size="small" color="primary" variant="outlined" label={p.invoiceNumber} />
                          {status !== 'paid' && (
                            <Button size="small" disabled={busy}
                              onClick={() => act(() => courierModuleService.unlinkPayoutInvoice(p.id), t('courierModule.payout.unlinkedInfo'))}>
                              {t('courierModule.payout.unlink')}
                            </Button>
                          )}
                        </Stack>
                      ) : p.accountId ? (
                        <Stack direction="row" spacing={1}>
                          <TextField select size="small" fullWidth label={t('courierModule.payout.linkInvoice')} value={invoiceId}
                            onChange={(e) => setInvoiceId(e.target.value)}>
                            {invoices.length === 0 && <MenuItem value="" disabled>{t('courierModule.payout.noInvoices')}</MenuItem>}
                            {invoices.map((i) => (
                              <MenuItem key={i.id} value={i.id}>{`${i.invoiceNumber} · ${trDate(i.invoiceDate)} · ${formatMoney(i.grandTotal)}`}</MenuItem>
                            ))}
                          </TextField>
                          <Button size="small" variant="outlined" disabled={busy || !invoiceId}
                            onClick={() => act(() => courierModuleService.linkPayoutInvoice(p.id, invoiceId), t('courierModule.payout.linkedInfo'))}>
                            {t('courierModule.payout.link')}
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{t('courierModule.payout.invoiceNeedsAccount')}</Typography>
                      )}
                    </>
                  )}

                  {saved && status === 'approved' && p.remaining > 0 && (
                    <>
                      <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>{t('courierModule.payout.payment')}</Typography>
                      <ToggleButtonGroup size="small" exclusive value={method} onChange={(_, v) => { if (v) { setMethod(v); setSourceId(''); } }}>
                        <ToggleButton value="bank">{t('courierModule.payout.methods.bank')}</ToggleButton>
                        <ToggleButton value="cash">{t('courierModule.payout.methods.cash')}</ToggleButton>
                        {isValet && <ToggleButton value="deduction">{t('courierModule.payout.methods.deduction')}</ToggleButton>}
                      </ToggleButtonGroup>
                      {method === 'bank' && (
                        <TextField select size="small" label={t('courierModule.payout.bank')} value={sourceId}
                          onChange={(e) => setSourceId(e.target.value === '' ? '' : Number(e.target.value))}>
                          {banks.map((b) => {
                            const id = b.id ?? b.Id;
                            return <MenuItem key={id} value={id}>{b.bankName ?? b.BankName}</MenuItem>;
                          })}
                        </TextField>
                      )}
                      {method === 'cash' && (
                        <TextField select size="small" label={t('courierModule.payout.cashbox')} value={sourceId}
                          onChange={(e) => setSourceId(e.target.value === '' ? '' : Number(e.target.value))}>
                          {cashboxes.map((c) => <MenuItem key={c.Id} value={c.Id}>{`${c.Name} · ${formatMoney(c.Balance)}`}</MenuItem>)}
                        </TextField>
                      )}
                      <TextField size="small" label={t('courierModule.payout.amount')} value={amount} placeholder={p.remaining.toFixed(2)}
                        onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
                        inputProps={{ inputMode: 'decimal' }}
                        InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }} />
                      <Button variant="contained" color="success" disabled={busy || (method !== 'deduction' && sourceId === '')} onClick={pay}>
                        {t('courierModule.payout.pay')}
                      </Button>
                    </>
                  )}

                  {saved && p.payments.length > 0 && (
                    <>
                      <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>{t('courierModule.payout.payments')}</Typography>
                      {p.payments.map((x, i) => (
                        <Typography key={i} variant="caption" color="text.secondary">
                          {`${trDateTime(x.at)} · ${formatMoney(x.amount)} · ${x.sourceName || t(`courierModule.payout.methods.${x.method}`)}${x.by ? ` · ${x.by}` : ''}`}
                        </Typography>
                      ))}
                    </>
                  )}
                  {saved && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {t('courierModule.payout.createdBy', { name: p.createdBy || '—', date: trDateTime(p.createdAt) })}
                      {p.approvedBy ? ` · ${t('courierModule.payout.approvedBy', { name: p.approvedBy, date: trDateTime(p.approvedAt) })}` : ''}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">{t('courierModule.payout.pnlHint')}</Typography>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        {p && saved && status === 'pending' && (
          <Button color="error" disabled={busy}
            onClick={() => act(() => courierModuleService.cancelPayout(p.id), t('courierModule.payout.cancelledInfo'))}>
            {t('courierModule.payout.cancel')}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{t('courierModule.actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: string; bold?: boolean; muted?: boolean }> = ({ label, value, bold, muted }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="body2" color={muted ? 'text.secondary' : 'text.primary'}>{label}</Typography>
    <Typography variant="body2" fontWeight={bold ? 700 : 400} color={muted ? 'text.secondary' : 'text.primary'}>{value}</Typography>
  </Box>
);

export default CourierPayoutDialog;
